/**
 * Pipeline Dates Composable
 * UX/UI Sprint 2 - Task 2.1
 *
 * Provides relative date formatting with contextual styling
 * Supports: created_at, deadline, due_date
 */

import { computed } from 'vue';
import {
  formatDistanceToNow,
  differenceInDays,
  isToday,
  isTomorrow,
  isYesterday,
  isPast,
  parseISO,
} from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

// Locale mapping
const locales = {
  'pt-BR': ptBR,
  pt_BR: ptBR,
  pt: ptBR,
  en: enUS,
  'en-US': enUS,
};

/**
 * Get the appropriate locale for date-fns.
 *
 * Card-4.6 (2026-04-29) — antes lia chatwootConfig.selectedLocale, que
 * é o default GLOBAL do app (env var), não o locale do usuário/conta.
 * Resultado: usuário em pt_BR via "Today", "In 12 days" em vez de
 * "Hoje", "Em 12 dias".
 *
 * Fix: ler do i18n instance global (exposto em window.__APP_I18N__
 * pelo entrypoint dashboard.js). Reativo: setLocale() em App.vue muda
 * `i18n.global.locale.value` e nossa função pega o valor atual a cada
 * chamada.
 */
const getLocale = () => {
  const userLocale =
    // eslint-disable-next-line no-underscore-dangle
    window.__APP_I18N__?.global?.locale?.value ||
    window.chatwootConfig?.selectedLocale ||
    document.documentElement.lang ||
    'pt-BR';
  return locales[userLocale] || ptBR;
};

/**
 * Parse a date string or Date object
 */
const parseDate = date => {
  if (!date) return null;
  if (date instanceof Date) return date;
  try {
    return parseISO(date);
  } catch {
    return new Date(date);
  }
};

/**
 * Check if a date string contains time information (not just 00:00:00)
 * @param {string} dateStr - The date string to check
 * @returns {boolean}
 */
const hasTimeInfo = dateStr => {
  if (!dateStr || typeof dateStr !== 'string') return false;
  // Check if it's just a date (YYYY-MM-DD) without time
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  // Check if it has time component that's not midnight
  const match = dateStr.match(/T(\d{2}):(\d{2})/);
  if (!match) return false;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  return hours !== 0 || minutes !== 0;
};

/**
 * Format time from a date
 * @param {Date} date - The date to format
 * @returns {string} Formatted time (HH:mm)
 */
const formatTime = date => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Format a relative date with contextual information
 * @param {Date|string} date - The date to format
 * @param {Object} options - Formatting options
 * @returns {Object} { text, status, color, time, fullDateTime }
 */
export const formatRelativeDate = (date, options = {}) => {
  const parsedDate = parseDate(date);
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return {
      text: null,
      status: 'invalid',
      color: null,
      time: null,
      fullDateTime: null,
    };
  }

  const locale = getLocale();
  const now = new Date();
  const daysDiff = differenceInDays(parsedDate, now);
  const isOverdue = isPast(parsedDate) && !isToday(parsedDate);

  // Check if the original date string has time information
  const includesTime = typeof date === 'string' && hasTimeInfo(date);
  const time = includesTime ? formatTime(parsedDate) : null;

  // Determine status and color
  let status = 'normal';
  let color = 'slate';

  if (options.isDeadline) {
    if (isOverdue) {
      status = 'overdue';
      color = 'red';
    } else if (isToday(parsedDate)) {
      status = 'due-today';
      color = 'orange';
    } else if (isTomorrow(parsedDate)) {
      status = 'due-tomorrow';
      color = 'yellow';
    } else if (daysDiff <= 3 && daysDiff > 0) {
      status = 'due-soon';
      color = 'blue';
    }
  }

  // Format text based on proximity
  let text;

  if (isToday(parsedDate)) {
    text = locale === ptBR ? 'Hoje' : 'Today';
  } else if (isTomorrow(parsedDate)) {
    text = locale === ptBR ? 'Amanhã' : 'Tomorrow';
  } else if (isYesterday(parsedDate)) {
    text = locale === ptBR ? 'Ontem' : 'Yesterday';
  } else if (isOverdue && options.isDeadline) {
    const absDays = Math.abs(daysDiff);
    if (absDays === 1) {
      text = locale === ptBR ? 'Atrasado 1 dia' : 'Overdue 1 day';
    } else {
      text =
        locale === ptBR
          ? `Atrasado ${absDays} dias`
          : `Overdue ${absDays} days`;
    }
  } else if (daysDiff > 0 && daysDiff <= 7) {
    text = locale === ptBR ? `Em ${daysDiff} dias` : `In ${daysDiff} days`;
  } else if (daysDiff < 0 && daysDiff >= -7) {
    const absDays = Math.abs(daysDiff);
    text = locale === ptBR ? `Há ${absDays} dias` : `${absDays} days ago`;
  } else {
    // Use date-fns for longer distances, capitalize first letter for consistency
    const raw = formatDistanceToNow(parsedDate, {
      locale,
      addSuffix: true,
    });
    text = raw.charAt(0).toUpperCase() + raw.slice(1);
  }

  // Include time in text if available
  const textWithTime = time ? `${text} ${time}` : text;

  // Format full date time for tooltip
  const fullDateTime = new Intl.DateTimeFormat(
    locale === ptBR ? 'pt-BR' : 'en-US',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: time ? '2-digit' : undefined,
      minute: time ? '2-digit' : undefined,
    }
  ).format(parsedDate);

  return { text: textWithTime, status, color, time, fullDateTime };
};

/**
 * Composable for pipeline card dates
 * @param {Ref} card - Reactive card object
 */
export const usePipelineDates = card => {
  // Card creation time (lifetime)
  const cardLifetime = computed(() => {
    const createdAt = card.value?.created_at;
    if (!createdAt) return null;

    const result = formatRelativeDate(createdAt, { isDeadline: false });
    return result.text;
  });

  // Deadline/due date with status
  const deadline = computed(() => {
    // Prefer deadline_at (with time support) over deadline (date only)
    const dueDate =
      card.value?.item_details?.deadline_at ||
      card.value?.item_details?.deadline ||
      card.value?.item_details?.due_date ||
      card.value?.deadline;

    if (!dueDate) return null;

    return formatRelativeDate(dueDate, { isDeadline: true });
  });

  // Scheduled date for activities/follow-ups
  const scheduledAt = computed(() => {
    const scheduled =
      card.value?.scheduled_at || card.value?.item_details?.scheduled_at;

    if (!scheduled) return null;

    return formatRelativeDate(scheduled, { isDeadline: false });
  });

  // Has scheduled date
  const hasScheduledDate = computed(() => {
    return scheduledAt.value?.text != null;
  });

  // CSS classes for deadline styling
  const deadlineClasses = computed(() => {
    if (!deadline.value) return '';

    const colorMap = {
      red: 'text-red-600 dark:text-red-400',
      orange: 'text-orange-600 dark:text-orange-400',
      yellow: 'text-yellow-600 dark:text-yellow-400',
      blue: 'text-blue-600 dark:text-blue-400',
      slate: 'text-slate-500 dark:text-slate-400',
    };

    return colorMap[deadline.value.color] || colorMap.slate;
  });

  // Background classes for deadline badge
  const deadlineBadgeClasses = computed(() => {
    if (!deadline.value) return '';

    const colorMap = {
      red: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
      orange:
        'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300',
      yellow:
        'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
      blue: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
      slate:
        'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
    };

    return colorMap[deadline.value.color] || colorMap.slate;
  });

  // Is the card overdue?
  const isOverdue = computed(() => {
    return deadline.value?.status === 'overdue';
  });

  // Is the card due soon (within 3 days)?
  const isDueSoon = computed(() => {
    const status = deadline.value?.status;
    return (
      status === 'due-today' ||
      status === 'due-tomorrow' ||
      status === 'due-soon'
    );
  });

  return {
    cardLifetime,
    deadline,
    deadlineClasses,
    deadlineBadgeClasses,
    isOverdue,
    isDueSoon,
    scheduledAt,
    hasScheduledDate,
  };
};

export default usePipelineDates;
